import {
  AmendmentPreview,
  PreviewLine,
  PreviewParagraph,
  ProcessedClauseChange,
  UnitPreview,
} from "common/flowmaster/types/amendment";
import {applyEdit} from "./segment_editor";
import {
  buildInsertedParagraphs,
  collapseUnchanged,
  markDeleted,
} from "./build_unit_preview";
import {formatUnitLabel, getUnitKey} from "./unit_key";

const LEGACY_FLAG = "amendmentPreview";

export function getMapPreview(
  change: ProcessedClauseChange,
): AmendmentPreview | null {
  const preview = change.rawAmendment?.preview;
  return preview && preview.unit_lines?.length > 0 ? preview : null;
}

// amendmentPreview=legacy in localStorage disables this path
export function isMapPreviewEnabled(): boolean {
  try {
    return window.localStorage.getItem(LEGACY_FLAG) !== "legacy";
  } catch {
    return true;
  }
}

function anchorLine(preview: AmendmentPreview): PreviewLine | undefined {
  return preview.unit_lines.find(line => line.line === preview.line);
}

function isNumbered(ref: string): boolean {
  return /^\d/.test(ref.trim());
}

// Unnumbered runs key on their first line
export function mapUnitKey(change: ProcessedClauseChange): string {
  const preview = getMapPreview(change);
  if (!preview) {
    return "";
  }
  const anchor = anchorLine(preview);
  const ref = anchor?.ref ?? "";
  const unit = isNumbered(ref)
    ? getUnitKey(ref)
    : `line-${preview.unit_lines[0].line}`;
  return `${preview.part_id}|${unit}`;
}

export function mapUnitLabel(change: ProcessedClauseChange): string {
  const preview = getMapPreview(change);
  if (!preview) {
    return change.clause_reference;
  }
  const anchor = anchorLine(preview);
  const ref = anchor?.ref ?? "";
  if (isNumbered(ref)) {
    return formatUnitLabel(getUnitKey(ref));
  }
  const first = preview.unit_lines[0];
  if (first.kind === "heading" && first.text.trim()) {
    return ref ? `${first.text.trim()} ${ref}` : first.text.trim();
  }
  return change.clause_reference;
}

// Layout markers from contract-texter, e.g. #DOCX_PAGE_BREAK#
const MARKER_LINE = /^#[A-Z0-9_]+#$/;

function mergedLines(changes: ProcessedClauseChange[]): PreviewLine[] {
  const byLine = new Map<number, PreviewLine>();
  changes.forEach(change => {
    getMapPreview(change)?.unit_lines.forEach(line => {
      if (!byLine.has(line.line) && !MARKER_LINE.test(line.text.trim())) {
        byLine.set(line.line, line);
      }
    });
  });
  return Array.from(byLine.values()).sort((a, b) => a.line - b.line);
}

function lineKey(line: number): string {
  return `line-${line}`;
}

function makeParagraph(line: PreviewLine, baseLevel: number): PreviewParagraph {
  return {
    key: lineKey(line.line),
    label: line.number_text,
    indent: Math.max(0, line.level - baseLevel),
    status: "kept",
    isHeading: line.kind === "heading",
    segments: [{kind: "kept", text: line.text}],
  };
}

function isWholeLine(preview: AmendmentPreview, text: string): boolean {
  return (
    preview.span !== undefined &&
    preview.span.start === 0 &&
    preview.span.end >= text.length
  );
}

// Right to left so earlier offsets hold
function applyInlineEdits(
  paragraphs: PreviewParagraph[],
  changes: ProcessedClauseChange[],
  unplaced: ProcessedClauseChange[],
): void {
  const byKey = new Map(
    paragraphs.map(paragraph => [paragraph.key, paragraph]),
  );
  const inline = changes
    .map(change => ({change, preview: getMapPreview(change)}))
    .filter(item => item.preview?.span !== undefined)
    .sort(
      (a, b) => (b.preview?.span?.start ?? 0) - (a.preview?.span?.start ?? 0),
    );
  inline.forEach(({change, preview}) => {
    if (!preview || !preview.span) {
      return;
    }
    const paragraph = byKey.get(lineKey(preview.line));
    if (!paragraph) {
      unplaced.push(change);
      return;
    }
    const text = paragraph.segments.map(segment => segment.text).join("");
    if (change.delete && isWholeLine(preview, text) && !change.added_text) {
      markDeleted(paragraph);
      return;
    }
    paragraph.segments = applyEdit(
      paragraph.segments,
      preview.span.start,
      preview.span.end,
      change.added_text ?? "",
    );
  });
}

function applyInserts(
  paragraphs: PreviewParagraph[],
  changes: ProcessedClauseChange[],
  unplaced: ProcessedClauseChange[],
): void {
  const inserts = changes
    .map(change => ({change, preview: getMapPreview(change)}))
    .filter(item => item.preview && item.preview.span === undefined)
    .sort((a, b) => (a.preview?.line ?? 0) - (b.preview?.line ?? 0));
  inserts.forEach(({change, preview}) => {
    if (!preview) {
      return;
    }
    const index = paragraphs.findIndex(
      paragraph => paragraph.key === lineKey(preview.line),
    );
    if (index === -1) {
      unplaced.push(change);
      return;
    }
    const anchor = anchorLine(preview);
    const unitKey =
      anchor && isNumbered(anchor.ref) ? getUnitKey(anchor.ref) : "";
    const inserted = buildInsertedParagraphs(
      change,
      unitKey,
      paragraphs[index].indent,
    );
    const at = preview.insert_before ? index : index + 1;
    paragraphs.splice(at, 0, ...inserted);
  });
}

export default function buildMapPreview(
  changes: ProcessedClauseChange[],
): UnitPreview {
  const lines = mergedLines(changes);
  const baseLevel = Math.min(...lines.map(line => line.level));
  const paragraphs = lines.map(line => makeParagraph(line, baseLevel));
  const unplaced: ProcessedClauseChange[] = [];
  applyInlineEdits(paragraphs, changes, unplaced);
  applyInserts(paragraphs, changes, unplaced);
  return {paragraphs: collapseUnchanged(paragraphs), unplaced};
}
